// Small presentational primitives shared across pages.
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useState } from 'react';
import { IconCopy, IconCheck } from './Icons';
import { useTranslation } from 'react-i18next';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: 'sm';
  block?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size, block, className, children, ...rest },
  ref,
) {
  const cls = [
    'btn',
    `btn--${variant}`,
    size === 'sm' ? 'btn--sm' : '',
    block ? 'btn--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button ref={ref} className={cls} {...rest}>
      {children}
    </button>
  );
});

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={['card', className ?? ''].join(' ')}>{children}</section>;
}

export function CardHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="card__head">
      <h2>{title}</h2>
      <div className="topbar__spacer" />
      {children}
    </div>
  );
}

export function Badge({
  children,
  tone = 'default',
}: {
  children: ReactNode;
  tone?: 'default' | 'accent' | 'success' | 'warning' | 'danger' | 'info';
}) {
  return <span className={`badge${tone !== 'default' ? ` badge--${tone}` : ''}`}>{children}</span>;
}

export function Stat({ label, value, icon }: { label: ReactNode; value: ReactNode; icon?: ReactNode }) {
  return (
    <div className="card stat">
      <div className="stat__label">
        {icon ? <span className="stat__icon">{icon}</span> : null}
        {label}
      </div>
      <div className="stat__value">{value}</div>
    </div>
  );
}

export function Spinner({ large }: { large?: boolean }) {
  return <span className={`spinner${large ? ' spinner--lg' : ''}`} role="status" aria-hidden="true" />;
}

/** Monospace identifier chip with copy-to-clipboard. IDs only ever appear here,
 *  never as primary navigation (UX mandate). */
export function IdChip({ id }: { id: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button
      type="button"
      className="id-chip"
      onClick={copy}
      title={id}
      aria-label={`${copied ? t('common.copied') : t('common.copy')}: ${id}`}
    >
      <span>{id}</span>
      {copied ? <IconCheck width={13} height={13} /> : <IconCopy width={13} height={13} />}
    </button>
  );
}
